import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PageHeader from '../../../components/PageHeader/PageHeader';
import AppConfigAdminService from '../../../services/AppConfigAdminService';
import { formatDateTime, policyError } from './appsUtils';

const PLATFORM_LABELS = { ANDROID: 'Android', IOS: 'iOS' };

const errorText = (e, fallback) => e?.response?.data?.error_message || e?.response?.data?.error || fallback;

const PolicyCard = ({ app, policy, onSaved, onError }) => {
  const [form, setForm] = useState(policy);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(policy), [policy]);

  const set = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));

  const save = async () => {
    const problem = policyError(form);
    if (problem) {
      onError(problem);
      return;
    }
    setSaving(true);
    try {
      await AppConfigAdminService.savePolicy(app.appKey, policy.platform, {
        minVersion: form.minVersion,
        suggestedVersion: form.suggestedVersion,
        latestVersion: form.latestVersion,
        storeUrl: form.storeUrl,
        updateMessage: form.updateMessage,
        maintenanceEnabled: form.maintenanceEnabled,
        maintenanceMessage: form.maintenanceMessage
      });
      onSaved();
    } catch (e) {
      onError(errorText(e, 'No se pudo guardar.'));
    } finally {
      setSaving(false);
    }
  };

  const platform = PLATFORM_LABELS[policy.platform] || policy.platform;
  return (
    <Card variant="outlined">
      <CardHeader
        title={platform}
        subheader={policy.updatedAt ? `Última modificación: ${formatDateTime(policy.updatedAt)}${policy.updatedBy ? ` · ${policy.updatedBy}` : ''}` : 'Sin configurar'}
      />
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Versiones
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Última publicada" value={form.latestVersion || ''} onChange={set('latestVersion')} placeholder="1.4.0" helperText="La que está hoy en la tienda" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Mínima (obliga)" value={form.minVersion || ''} onChange={set('minVersion')} placeholder="1.2.0" helperText="Por debajo, la app se bloquea" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Sugerida (avisa)" value={form.suggestedVersion || ''} onChange={set('suggestedVersion')} placeholder="1.4.0" helperText="Por debajo, avisa y deja seguir" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Enlace de la tienda" value={form.storeUrl || ''} onChange={set('storeUrl')} placeholder="https://play.google.com/store/apps/details?id=..." />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Mensaje de actualización (opcional)" value={form.updateMessage || ''} onChange={set('updateMessage')} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Mantenimiento
        </Typography>
        <FormControlLabel
          control={<Switch checked={Boolean(form.maintenanceEnabled)} onChange={(e) => setForm((c) => ({ ...c, maintenanceEnabled: e.target.checked }))} />}
          label="Frenar la app con un aviso de mantenimiento"
        />
        {form.maintenanceEnabled && (
          <>
            <Alert severity="warning" sx={{ mt: 1 }}>
              Mientras esté activo, nadie puede usar la app en {platform}.
            </Alert>
            <TextField fullWidth multiline minRows={2} sx={{ mt: 2 }} label="Mensaje de mantenimiento (opcional)" value={form.maintenanceMessage || ''} onChange={set('maintenanceMessage')} />
          </>
        )}

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <LoadingButton variant="contained" onClick={save} loading={saving}>
            Guardar {platform}
          </LoadingButton>
        </Box>
      </CardContent>
    </Card>
  );
};

// Documentos de cada app. Se publican en la landing como https://axium.com.uy/<app>/<ruta>.
const LANDING_URL = 'https://axium.com.uy';
const DOCUMENTS = [
  {
    type: 'TERMS',
    slug: 'terms',
    path: 'terminos',
    label: 'Términos y condiciones',
    hint: 'Cada usuario tiene que aceptar la versión vigente al abrir la app, antes de seguir. Aceptan por usuario y por versión.',
    asksAcceptance: true
  },
  {
    type: 'PRIVACY',
    slug: 'privacy',
    path: 'privacidad',
    label: 'Política de privacidad',
    hint: 'No pide aceptar nada: la app enlaza a esta página. Si no cargás una propia, se muestra la general de Axium.'
  },
  {
    type: 'DATA_DELETION',
    slug: 'data-deletion',
    path: 'eliminar-datos',
    label: 'Eliminar datos',
    hint: 'Cómo pedir que se borren los datos. La app enlaza a esta página. Si no cargás una propia, se muestra la general de Axium.'
  }
];

const DocumentEditor = ({ app, doc, versions, onPublished, onError }) => {
  const active = versions.find((v) => v.isActive);
  const [text, setText] = useState(active?.text || '');
  const [summary, setSummary] = useState('');
  const [publishing, setPublishing] = useState(false);
  useEffect(() => {
    setText(active?.text || '');
    setSummary('');
  }, [active?.version, active?.text]);

  const publish = async () => {
    if (!text.trim()) {
      onError('Escribí el texto antes de publicar.');
      return;
    }
    setPublishing(true);
    try {
      await AppConfigAdminService.publishDocument(app.appKey, doc.slug, text, summary);
      onPublished(doc.label);
    } catch (e) {
      onError(errorText(e, 'No se pudo publicar.'));
    } finally {
      setPublishing(false);
    }
  };

  const url = `${LANDING_URL}/${app.appKey.toLowerCase()}/${doc.path}`;
  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        {doc.hint} Se publica en <strong>{url}</strong>. El texto es plano: una línea en blanco separa párrafos y una línea que empieza con «## » es un título.
      </Alert>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {active
          ? `Versión ${active.version} vigente${doc.asksAcceptance ? ` · aceptada por ${active.acceptances} usuario(s)` : ''}`
          : 'Todavía no hay una versión propia de esta app.'}
      </Typography>
      <TextField fullWidth multiline minRows={12} label={doc.label} value={text} onChange={(e) => setText(e.target.value)} />
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label="Qué cambió (opcional)"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        helperText={doc.asksAcceptance ? 'Se le muestra al usuario al pedirle que acepte la versión nueva.' : 'Solo para el historial.'}
      />
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <LoadingButton variant="contained" onClick={publish} loading={publishing}>
          Publicar nueva versión
        </LoadingButton>
      </Box>
      {versions.length > 0 && (
        <Table size="small" sx={{ mt: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Versión</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Qué cambió</TableCell>
              {doc.asksAcceptance && <TableCell>Aceptaciones</TableCell>}
              <TableCell>Publicada</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((v) => (
              <TableRow key={v.version}>
                <TableCell>{v.version}</TableCell>
                <TableCell>{v.isActive ? <Chip size="small" color="success" label="Vigente" /> : 'Reemplazada'}</TableCell>
                <TableCell>{v.changeSummary || '—'}</TableCell>
                {doc.asksAcceptance && <TableCell>{v.acceptances}</TableCell>}
                <TableCell>
                  {formatDateTime(v.createdAt)}
                  {v.publishedBy ? ` · ${v.publishedBy}` : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

const DocumentsCard = ({ app, onPublished, onError }) => {
  const [all, setAll] = useState([]);
  const [docTab, setDocTab] = useState(0);

  const load = useCallback(async () => {
    setAll(await AppConfigAdminService.getDocuments(app.appKey));
  }, [app.appKey]);

  useEffect(() => {
    load().catch(() => setAll([]));
  }, [load]);

  const doc = DOCUMENTS[docTab];
  return (
    <Card variant="outlined">
      <CardHeader title="Documentos de la app" subheader="Términos, privacidad y eliminación de datos, propios de esta app y con versiones." />
      <CardContent>
        <Tabs value={docTab} onChange={(_, value) => setDocTab(value)} sx={{ mb: 2 }}>
          {DOCUMENTS.map((d) => (
            <Tab key={d.type} label={d.label} />
          ))}
        </Tabs>
        <DocumentEditor
          key={`${app.appKey}-${doc.type}`}
          app={app}
          doc={doc}
          versions={all.filter((v) => v.type === doc.type)}
          onError={onError}
          onPublished={async (label) => {
            await load();
            onPublished(label);
          }}
        />
      </CardContent>
    </Card>
  );
};

const AppsPage = () => {
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const load = useCallback(async () => {
    try {
      setApps(await AppConfigAdminService.getApps());
      setLoadError('');
    } catch (e) {
      setLoadError(errorText(e, 'No se pudieron cargar las apps.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const app = apps[tab];

  return (
    <Box p={3}>
      <PageHeader
        title="Apps"
        subtitle="Versiones obligatorias o sugeridas, mantenimiento y términos y condiciones de cada app móvil."
      />
      {loadError && <Alert severity="error" sx={{ mt: 2 }}>{loadError}</Alert>}
      {!loading && apps.length > 0 && (
        <>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mt: 2, mb: 2 }} variant="scrollable">
            {apps.map((a) => (
              <Tab key={a.appKey} label={a.name} />
            ))}
          </Tabs>
          {errorMessage && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>{errorMessage}</Alert>}
          <Typography variant="h6" gutterBottom>Versiones y mantenimiento</Typography>
          <Grid container spacing={2}>
            {app.policies.map((policy) => (
              <Grid item xs={12} xl={6} key={`${app.appKey}-${policy.platform}`}>
                <PolicyCard
                  app={app}
                  policy={policy}
                  onError={setErrorMessage}
                  onSaved={() => {
                    setErrorMessage('');
                    setMessage('Guardado');
                    load();
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Box mt={3}>
            <DocumentsCard
              key={app.appKey}
              app={app}
              onError={setErrorMessage}
              onPublished={(label) => {
                setErrorMessage('');
                setMessage(`${label}: nueva versión publicada`);
                load();
              }}
            />
          </Box>
        </>
      )}
      <Snackbar open={Boolean(message)} autoHideDuration={2500} onClose={() => setMessage('')}>
        <Alert severity="success" onClose={() => setMessage('')}>{message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AppsPage;
